import { useMutation, useQuery } from "@tanstack/react-query"
import type { ProductType } from "@tfg/db"
import { AxiosError } from "axios"

import { AdminResponses } from "@tfg/types/endpoints/admin"
import {
  EcobalyseCountry,
  EcobalyseMaterialType,
} from "@tfg/types/endpoints/analysis"
import { LibraryResponses } from "@tfg/types/endpoints/library"
import { materialTypeRequestValidationType } from "@tfg/types/schemas/admin"
import { componentUpdateNameValidationType } from "@tfg/types/schemas/components"
import { materialUpdateValidationType } from "@tfg/types/schemas/materials"
import { updateAgecShowValidationType } from "@tfg/types/schemas/products"

import { httpClient } from "~/backend/axios.ts"
import { queryClient } from "~/backend/queryClient.ts"

export const useProductQuery = (productId: number) => {
  return useQuery<LibraryResponses.ProductDetails>(
    ["products", { productId }],
    async () => {
      const response = await httpClient.get(`products/${productId}`)
      return response.data
    }
  )
}

export const useUpdateAgecShowMutation = (productId: number) => {
  return useMutation<unknown, unknown, updateAgecShowValidationType>(
    ["products", productId],
    async (variables) => {
      const response = await httpClient.patch(
        `products/agec-show/${productId}`,
        variables
      )
      return response.data
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ["products", { productId }],
        })
      },
    }
  )
}

export const useDeleteProductMutation = (productId: number) => {
  return useMutation(
    ["products", productId],
    async () => {
      const response = await httpClient.delete(`products/${productId}`)
      return response.data
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(["products"])
      },
    }
  )
}

export const useProductTypesQuery = () => {
  return useQuery<ProductType[]>(["productTypes"], async () => {
    const response = await httpClient.get(`products/types`)
    return response.data
  })
}

export const useComponentUpdateNameMutation = () =>
  useMutation<void, AxiosError, componentUpdateNameValidationType>({
    mutationFn: async (variables) => {
      const response = await httpClient.patch(`components/name`, variables)
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries(["products"])
    },
  })

export const useListMaterialTypesQuery = (materialOpChain: string) =>
  useQuery(["materialTypes", materialOpChain], async () => {
    const params: materialTypeRequestValidationType = {
      materialOpChainName: materialOpChain,
      showAll: true,
    }
    const response = await httpClient.get<AdminResponses.MaterialTypes[]>(
      "/admin/material-types",
      {
        params,
      }
    )
    return response.data
  })

export const useUpdateMaterialMutation = () =>
  useMutation<void, AxiosError, materialUpdateValidationType>({
    mutationFn: async (variables) => {
      const response = await httpClient.patch(`materials`, variables)
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries(["products"])
    },
  })

export const useListEcobalyseMaterialTypesQuery = () =>
  useQuery(["ecobalyse", "materialTypes"], async () => {
    const res = await httpClient.get<EcobalyseMaterialType[]>(
      "/analysis/materialTypes"
    )

    return res.data
  })

export const useListEcobalyseSupportedCountriesQuery = () =>
  useQuery(["ecobalyse", "countries"], async () => {
    const res = await httpClient.get<EcobalyseCountry[]>("/analysis/countries")

    return res.data
  })
